import { Component } from "react";
import { AlertTriangle, RotateCcw } from "lucide-react";

/**
 * Catch a render failure and show it, rather than a white screen.
 *
 * React unmounts the whole tree when a render throws, so one bad field
 * reference took the entire page with it - a container that reported its
 * destination as `post_offices` when the packer produces `post_office_name`
 * blanked the screen the moment anyone chose Boxes, with nothing on it to say
 * what had happened or what to do next.
 *
 * The page still cannot render, but the person is told which screen failed,
 * given the message that a bug report needs, and offered a way out that does
 * not involve guessing at the browser's back button.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // The stack is what makes this diagnosable; the console is where someone
    // will look for it.
    console.error("Render failed:", error, info?.componentStack);
  }

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div className="page-container">
        <div className="card" style={{ maxWidth: "40rem", margin: "3rem auto" }}>
          <h2 className="card-title">
            <AlertTriangle size={20} />
            This screen could not be displayed
          </h2>

          <p className="muted">
            Something on this page failed to render. Your data has not been
            changed — nothing was saved or sent.
          </p>

          <pre className="error-detail">{String(this.state.error?.message ?? this.state.error)}</pre>

          <div className="button-row" style={{ marginTop: "1.25rem" }}>
            <button className="btn btn-primary" onClick={() => window.location.reload()}>
              <RotateCcw size={16} />
              Reload the page
            </button>
            <button
              className="btn btn-outline"
              onClick={() => {
                this.setState({ error: null });
                window.history.back();
              }}
            >
              Go back
            </button>
          </div>

          <p className="field-hint" style={{ marginTop: "1rem" }}>
            If this keeps happening, the message above is what to report.
          </p>
        </div>
      </div>
    );
  }
}
